import { useState } from 'react';
import { apiClient } from '../../auth-lib';
import { Button, Input, Select, Modal, useToast } from '../../design-system';
import HoursMinutesInput, { splitMinutes, joinMinutes } from './HoursMinutesInput';

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

// Edit an existing to-do: title, remark, priority, assignee, due date and time
// budget. Status stays on the row's inline Select (it's the one field people
// change constantly) and time LOGGING stays in TodoTimeModal — this modal only
// edits the to-do itself.
//
// `assigneeFilter` mirrors the add form's: the server only accepts yourself or
// someone below your level, so offering the whole org here would just produce a
// 403 the add form already prevents.
function TodoEditModal({ todo, orgUsers = [], onClose, onUpdated, assigneeFilter = null }) {
  const toast = useToast();

  const initial = splitMinutes(todo.allocatedMinutes);
  const [title, setTitle] = useState(todo.title || '');
  const [remark, setRemark] = useState(todo.remark || '');
  const [assignee, setAssignee] = useState(todo.assignee?._id || '');
  const [priority, setPriority] = useState(todo.priority || 'medium');
  const [dueDate, setDueDate] = useState(
    todo.dueDate ? new Date(todo.dueDate).toISOString().slice(0, 10) : ''
  );
  const [allocH, setAllocH] = useState(initial.hours);
  const [allocM, setAllocM] = useState(initial.minutes);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    const t = title.trim();
    if (!t) {
      setError('Title is required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const res = await apiClient.put(`/api/todos/${todo._id}`, {
        title: t,
        // Sent even when empty — '' is how a remark gets cleared.
        remark: remark.trim(),
        assignee: assignee || null,
        priority,
        dueDate: dueDate || null,
        allocatedMinutes: joinMinutes(allocH, allocM),
      });
      onUpdated(res.data);
      toast.success('To-do updated');
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update to-do');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Edit to-do"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={save} loading={saving}>Save</Button>
        </>
      }
    >
      <div className="todo-edit-form">
        <Input
          label="Title"
          value={title}
          autoFocus
          onChange={(e) => setTitle(e.target.value)}
        />

        <div className="ds-field">
          <label className="ds-field-label" htmlFor="todo-remark">Remark</label>
          <textarea
            id="todo-remark"
            className="ds-input"
            rows="3"
            placeholder="Add a note — e.g. waiting on the client's invoices"
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
          />
        </div>

        <Select label="Priority" value={priority} onChange={(e) => setPriority(e.target.value)}>
          {PRIORITY_OPTIONS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
        </Select>

        <Select label="Assignee" value={assignee} onChange={(e) => setAssignee(e.target.value)}>
          <option value="">Unassigned</option>
          {(assigneeFilter ? orgUsers.filter(assigneeFilter) : orgUsers).map((u) => (
            <option key={u._id} value={u._id}>{u.name}</option>
          ))}
        </Select>

        <Input
          label="Due date"
          type="date"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
        />

        <HoursMinutesInput
          label="Allocated time"
          hours={allocH}
          minutes={allocM}
          onChange={(h, m) => { setAllocH(h); setAllocM(m); }}
          helperText="Leave both empty for no budget"
        />

        {error && <p className="todo-edit-error">{error}</p>}
      </div>
    </Modal>
  );
}

export default TodoEditModal;
